import React, { useEffect, useRef, useState, forwardRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

export const RecordingAudioPlayer = forwardRef(function RecordingAudioPlayer(
  { recording_sid, is_active, on_play },
  forwarded_ref,
) {
  const [blob_url, setBlobUrl] = useState(null);
  const [audio_loading, setAudioLoading] = useState(true);
  const [audio_error, setAudioError] = useState(false);
  const audio_ref = useRef(null);

  const set_audio_ref = (node) => {
    audio_ref.current = node;
    if (typeof forwarded_ref === 'function') forwarded_ref(node);
    else if (forwarded_ref) forwarded_ref.current = node;
  };

  useEffect(() => {
    if (!recording_sid) return;

    let object_url = null;
    let cancelled = false;

    setAudioLoading(true);
    setAudioError(false);
    setBlobUrl(null);

    const src = `${process.env.REACT_APP_REGIONAL_BRANCH_BACKEND_API_URL}/twilio/recordings/${encodeURIComponent(recording_sid)}/audio`;

    const token = (() => {
      try {
        const info = localStorage.getItem('loginInfo');
        return info ? JSON.parse(info).token : '';
      } catch (_) { return ''; }
    })();

    fetch(src, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        object_url = URL.createObjectURL(blob);
        setBlobUrl(object_url);
        setAudioLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAudioError(true);
          setAudioLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (object_url) URL.revokeObjectURL(object_url);
    };
  }, [recording_sid]);

  useEffect(() => {
    if (is_active) return;
    const audio = audio_ref.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [is_active]);

  if (audio_loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={14} sx={{ color: '#9CA3AF' }} />
        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#9CA3AF' }}>
          Loading audio…
        </Typography>
      </Box>
    );
  }

  if (audio_error || !blob_url) {
    return (
      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#DC2626' }}>
        Failed to load audio.
      </Typography>
    );
  }

  return (
    <audio
      ref={set_audio_ref}
      controls
      src={blob_url}
      onPlay={() => on_play?.(recording_sid)}
      style={{ width: '100%', display: 'block', borderRadius: '8px' }}
    />
  );
});
